<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import OverlayBadge from 'primevue/overlaybadge';
import PaperclipIcon from '@primeicons/vue/paperclip';
import { useToastNotifications, useUtilities } from '@apparatix/composables';
import type { ActivityFile } from '@apparatix/schemas/Activities';
import { Noir } from '@apparatix/theme/Noir';

type Props = {
  files: ActivityFile[],
};

const props = defineProps<Props>();

const { arrayHasValues, downloadFile, getMimeTypeForFilename } = useUtilities();
const { showError } = useToastNotifications();

const menuRef = ref();

const downloadFileCommand = (file: ActivityFile) => {
  try {
    const filename = file.location.split('/').pop();
    const mimeType = getMimeTypeForFilename(filename);
    const url = `${window.location.origin}/${file.location}`;
    downloadFile(url, file.displayName, mimeType, true);
  } catch {
    showError(`Unable to download ${file.displayName}. Please try again, or contact support`, 'Error');
  }
};

const menuItems = computed<MenuItem[]>(() =>
  arrayHasValues(props.files) ? props.files.map(file => ({ label: file.displayName, command: () => downloadFileCommand(file) })) : [],
);


const onClick = (e: MouseEvent) => {
  if (arrayHasValues(props.files, { exactly: 1 })) {
    downloadFileCommand(props.files[0]);
  } else {
    menuRef.value.toggle(e);
  }
};
</script>
<template>
  <OverlayBadge :value="props.files.length" :dt="Noir.badge">
    <Button
      icon-only
      severity="secondary"
      :dt="Noir.button"
      rounded
      @click="onClick"
    >
      <PaperclipIcon />
    </Button>
  </OverlayBadge>
  <Menu ref="menuRef" :model="menuItems" popup />
</template>
